import React, { useState, useEffect, useMemo } from 'react'

import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'

import EditableRegionFormOverview from './editableRegionFormOverview'
import EditableRegionFormValidation from './editableRegionFormValidation'
import EditableRegionFormOptions from './editableRegionFormOptions'
import EditableRegionFormOptionsLarge from './editableRegionFormOptionsLarge'
import EditableRegionFormMerge from './editableRegionFormMerge'
import EditableRegionFormPreview from './editableRegionFormPreview'
import AdminCard from './adminCard'
import ActionBar from './actionBar'
import EditableRegionPrePopulate from './editableRegionPrePopulate'

import {
    checkNameIsValid,
    checkInputTypeIdIsValid,
    checkEditableIsValid,
    checkMinLengthIsValid,
    checkMaxLengthIsValid,
    checkOptionsAreValid,
} from '../services/editableregion.service'
import editabletypesConstants from '../constants/editabletypes.constants'
import PropTypes from 'prop-types'

const EditableRegionForm = ({
    editableRegion,
    mergeFields,
    currentEditables,
    onSubmit,
}) => {
    const [currentEditableRegion, setCurrentEditableRegion] = useState(
        editableRegion
    )
    const [isValid, setIsValid] = useState(false)
    const [isEditMode] = useState(editableRegion.id > 0)
    const [steps, setSteps] = useState([])
    const [activeStep, setActiveStep] = useState(0)
    const [isActiveStepValid, setIsActiveStepValid] = useState(false)

    const [prePopulatedHtml, setPrePopulatedHtml] = useState(
        editableRegion.prePopulatedState
    )

    useEffect(() => {
        setPrePopulatedHtml(editableRegion.prePopulatedState)
    }, [editableRegion])

    useEffect(() => {
        setCurrentEditableRegion({
            ...currentEditableRegion,
            prePopulatedState: prePopulatedHtml,
        })
    }, [prePopulatedHtml])

    useEffect(
        () =>
            setIsValid(
                checkEditableIsValid(currentEditableRegion, currentEditables)
            ),
        [currentEditableRegion]
    )

    useEffect(() => {
        let steps = []
        switch (currentEditableRegion.inputTypeId) {
            case editabletypesConstants.DatePicker:
            case editabletypesConstants.TimePicker:
                steps = !isEditMode
                    ? [
                          'Create an editable',
                          'Select merge field',
                          'Preview and confirm',
                      ]
                    : [
                          'Update an editable',
                          'Update merge field',
                          'Preview and confirm',
                      ]
                break
            case editabletypesConstants.Dropdown:
            case editabletypesConstants.Carousel:
                steps = !isEditMode
                    ? [
                          'Create an editable',
                          'Select options',
                          'Preview and confirm',
                      ]
                    : [
                          'Update an editable',
                          'Select options',
                          'Preview and confirm',
                      ]
                break
            case editabletypesConstants.FreeText:
            case editabletypesConstants.Address:
                steps = !isEditMode
                    ? [
                          'Create an editable',
                          'Set character limit',
                          'Select merge field',
                          'Preview and confirm',
                      ]
                    : [
                          'Update an editable',
                          'Update character limit',
                          'Update merge field',
                          'Preview and confirm',
                      ]
                break
            case editabletypesConstants.HtmlEditor:
                steps = !isEditMode
                    ? [
                          'Create an editable',
                          'Pre-populate text',
                          'Preview and confirm',
                      ]
                    : [
                          'Update an editable',
                          'Pre-populate text',
                          'Preview and confirm',
                      ]
                break
            default:
                steps = ['Create an editable']
        }
        setSteps(steps)
    }, [isEditMode, currentEditableRegion.inputTypeId])

    useEffect(() => setIsActiveStepValid(validateActiveStep()), [
        currentEditableRegion,
        activeStep,
    ])

    const validateActiveStep = () => {
        if (activeStep === 0) {
            return validateStep0()
        }

        if (isInputTypeWithValidation(currentEditableRegion.inputTypeId)) {
            if (activeStep === 1) return validateStep1()
            if (activeStep === 2) return true //NO VALIDATION ON MERGE FIELD
        }

        if (isInputTypeWithOptions(currentEditableRegion.inputTypeId)) {
            if (activeStep === 1) return validateOptions()
        }

        if (
            currentEditableRegion.inputTypeId ===
            editabletypesConstants.HtmlEditor
        )
            return true

        if (isDateOrTimePicker(currentEditableRegion.inputTypeId)) {
            if (activeStep === 1) return true
        }

        return false
    }

    const validateStep0 = () => {
        const nameValidity = checkNameIsValid(
            currentEditableRegion.name,
            currentEditables
        )
        return (
            nameValidity.isValid &&
            checkInputTypeIdIsValid(currentEditableRegion.inputTypeId)
        )
    }

    const validateStep1 = () => {
        return (
            checkMinLengthIsValid(
                currentEditableRegion.inputTypeId,
                currentEditableRegion.minLength,
                currentEditableRegion.maxLength
            ) &&
            checkMaxLengthIsValid(
                currentEditableRegion.inputTypeId,
                currentEditableRegion.minLength,
                currentEditableRegion.maxLength
            )
        )
    }

    const validateOptions = () => {
        return checkOptionsAreValid(
            currentEditableRegion.inputTypeId,
            currentEditableRegion.editableRegionOptions
        )
    }

    const isInputTypeWithValidation = inputTypeId => {
        return (
            inputTypeId === editabletypesConstants.FreeText ||
            inputTypeId === editabletypesConstants.Address
        )
    }

    const isInputTypeWithOptions = inputTypeId => {
        return (
            inputTypeId === editabletypesConstants.Dropdown ||
            inputTypeId === editabletypesConstants.Carousel
        )
    }

    const isDateOrTimePicker = inputTypeId => {
        return [
            editabletypesConstants.DatePicker,
            editabletypesConstants.TimePicker,
        ].includes(inputTypeId)
    }

    const handleOnSubmit = e => {
        e.preventDefault()
        if (isValid) {
            onSubmit({
                ...currentEditableRegion,
                mergeFieldId:
                    currentEditableRegion.mergeFieldId === 0
                        ? null
                        : currentEditableRegion.mergeFieldId, //SET TO NULL IF 0
            })
        }
    }

    const handleMergeFieldIdChanged = val => {
        const parsedVal =
            val === null || val === undefined || val === '' ? 0 : parseInt(val)

        setCurrentEditableRegion(prev => ({
            ...prev,
            mergeFieldId: parsedVal,
            ...(parsedVal === 0 && {
                editableMergeFieldTypeValue: null,
                editableMergeFieldType: null,
            }),
        }))
    }

    const handleSelectingEventId = (val, editableMergeFieldType = null) => {
        setCurrentEditableRegion(prev => ({
            ...prev,
            editableMergeFieldTypeValue: val != null ? String(val) : null,
            editableMergeFieldType: val != null ? editableMergeFieldType : null,
        }))
    }

    return (
        <>
            <AdminCard>
                <Stepper activeStep={activeStep}>
                    {steps.map(label => {
                        return (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        )
                    })}
                </Stepper>
            </AdminCard>
            <form onSubmit={handleOnSubmit}>
                {activeStep === 0 && (
                    <EditableRegionFormOverview
                        id={currentEditableRegion.id}
                        name={currentEditableRegion.name}
                        nameChanged={val =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                name: val,
                            })
                        }
                        inputTypeId={currentEditableRegion.inputTypeId}
                        typeChanged={val =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                inputTypeId: val,
                            })
                        }
                        inputCustomFormat={
                            currentEditableRegion.inputCustomFormat
                        }
                        inputCustomFormatChanged={inputCustomFormat =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                inputCustomFormat,
                            })
                        }
                        description={currentEditableRegion.description}
                        descriptionChanged={description =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                description,
                            })
                        }
                        testData={currentEditableRegion.testData}
                        testDataChanged={testData =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                testData,
                            })
                        }
                        minLength={currentEditableRegion.minLength}
                        maxLength={currentEditableRegion.maxLength}
                        isEditMode={isEditMode}
                        isMandatory={currentEditableRegion.isMandatory}
                        isMandatoryChanged={val =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                isMandatory: val,
                            })
                        }
                        isArchived={currentEditableRegion.isArchived}
                        isArchivedChanged={val =>
                            setCurrentEditableRegion({
                                ...currentEditableRegion,
                                isArchived: val,
                            })
                        }
                        currentEditables={currentEditables}
                    />
                )}
                {(currentEditableRegion.inputTypeId ===
                    editabletypesConstants.FreeText ||
                    currentEditableRegion.inputTypeId ===
                        editabletypesConstants.Address) &&
                    activeStep === 1 && (
                        <EditableRegionFormValidation
                            inputTypeId={currentEditableRegion.inputTypeId}
                            minLength={currentEditableRegion.minLength}
                            minLengthChanged={val =>
                                setCurrentEditableRegion({
                                    ...currentEditableRegion,
                                    minLength: parseInt(val),
                                })
                            }
                            maxLength={currentEditableRegion.maxLength}
                            maxLengthChanged={val =>
                                setCurrentEditableRegion({
                                    ...currentEditableRegion,
                                    maxLength: parseInt(val),
                                })
                            }
                        />
                    )}
                {currentEditableRegion.inputTypeId ===
                    editabletypesConstants.Dropdown &&
                    activeStep === 1 && (
                        <EditableRegionFormOptions
                            id={currentEditableRegion.id}
                            editableRegionName={currentEditableRegion.name}
                            options={
                                currentEditableRegion.editableRegionOptions
                            }
                            optionsChanged={val =>
                                setCurrentEditableRegion({
                                    ...currentEditableRegion,
                                    editableRegionOptions: val,
                                })
                            }
                        />
                    )}
                {currentEditableRegion.inputTypeId ===
                    editabletypesConstants.Carousel &&
                    activeStep === 1 && (
                        <EditableRegionFormOptionsLarge
                            id={currentEditableRegion.id}
                            options={
                                currentEditableRegion.editableRegionOptions
                            }
                            optionsChanged={val =>
                                setCurrentEditableRegion({
                                    ...currentEditableRegion,
                                    editableRegionOptions: val,
                                })
                            }
                        />
                    )}
                {(((currentEditableRegion.inputTypeId ===
                    editabletypesConstants.FreeText ||
                    currentEditableRegion.inputTypeId ===
                        editabletypesConstants.Address) &&
                    activeStep === 2) ||
                    ([
                        editabletypesConstants.DatePicker,
                        editabletypesConstants.TimePicker,
                    ].includes(currentEditableRegion.inputTypeId) &&
                        activeStep === 1)) && (
                    <EditableRegionFormMerge
                        mergeFields={mergeFields}
                        editableMergeFieldTypeValue={
                            currentEditableRegion.editableMergeFieldTypeValue
                        }
                        mergeFieldId={currentEditableRegion.mergeFieldId}
                        mergeFieldIdChanged={handleMergeFieldIdChanged}
                        onSelectingEventId={handleSelectingEventId}
                    />
                )}
                {currentEditableRegion.inputTypeId ===
                    editabletypesConstants.HtmlEditor &&
                    activeStep === 1 && (
                        <EditableRegionPrePopulate
                            html={prePopulatedHtml}
                            htmlChanged={setPrePopulatedHtml}
                        />
                    )}
                {currentEditableRegion.inputTypeId > 0 &&
                    isValid &&
                    activeStep === steps.length - 1 && (
                        <EditableRegionFormPreview
                            editableRegion={currentEditableRegion}
                            prePopulatedState={prePopulatedHtml}
                        />
                    )}
                <ActionBar
                    leftContent={[
                        {
                            label: 'Back',
                            show: activeStep !== 0,
                            isPrimaryAction: false,
                            onClick: () =>
                                setActiveStep(
                                    prevActiveStep => prevActiveStep - 1
                                ),
                        },
                        {
                            label: 'Next',
                            show: activeStep !== steps.length - 1,
                            isPrimaryAction: false,
                            disabled: !isActiveStepValid,
                            onClick: () =>
                                setActiveStep(
                                    prevActiveStep => prevActiveStep + 1
                                ),
                        },
                    ]}
                    rightContent={[
                        {
                            label: isEditMode ? 'Update' : 'Create',
                            show: activeStep === steps.length - 1,
                            isPrimaryAction: true,
                            disabled: !isValid,
                            type: 'submit',
                        },
                    ]}
                />
            </form>
        </>
    )
}
EditableRegionForm.propTypes = {
    editableRegion: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        inputTypeId: PropTypes.number.isRequired,
        prePopulatedState: PropTypes.string,
        minLength: PropTypes.number,
        maxLength: PropTypes.number,
    }).isRequired,
    mergeFields: PropTypes.array.isRequired,
    currentEditables: PropTypes.array.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default EditableRegionForm
